import { useEffect, useState } from 'react'
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Cell,
} from 'recharts'
import api from '../services/api'

type Period = '30d' | '90d' | 'ytd' | '1y'

const AU_STATES = [
  { code: 'QLD', name: 'Queensland' },
  { code: 'NSW', name: 'New South Wales' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'SA', name: 'South Australia' },
  { code: 'WA', name: 'Western Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'NT', name: 'N. Territory' },
  { code: 'ACT', name: 'ACT' },
]

function periodDates(p: Period): { start: string; end: string } {
  const now = new Date()
  const end = now.toISOString().slice(0, 10)
  const y = now.getFullYear()
  if (p === '30d') {
    const s = new Date(now); s.setDate(s.getDate() - 30)
    return { start: s.toISOString().slice(0, 10), end }
  }
  if (p === '90d') {
    const s = new Date(now); s.setDate(s.getDate() - 90)
    return { start: s.toISOString().slice(0, 10), end }
  }
  if (p === 'ytd') return { start: `${y}-01-01`, end }
  return { start: `${y - 1}-${now.toISOString().slice(5, 10)}`, end }
}

function severityColour(v: number | null, metric: string): string {
  if (v === null) return 'var(--text-muted)'
  if (metric === 'trifr') return v === 0 ? 'var(--green)' : v < 3 ? 'var(--amber)' : 'var(--red)'
  if (metric === 'ltifr') return v === 0 ? 'var(--green)' : v < 2 ? 'var(--amber)' : 'var(--red)'
  if (metric === 'days') return v > 365 ? 'var(--green)' : v > 90 ? 'var(--amber)' : 'var(--red)'
  return 'var(--text)'
}

function KPICard({ label, value, unit, colour, sub }: { label: string; value: number | null; unit?: string; colour?: string; sub?: string }) {
  return (
    <div className="card text-center py-5">
      <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>{label}</p>
      <p className="text-4xl font-black" style={{ color: colour || 'var(--text)' }}>
        {value === null ? '—' : value.toLocaleString()}
        {unit && <span className="text-sm font-normal ml-1" style={{ color: 'var(--text-muted)' }}>{unit}</span>}
      </p>
      {sub && <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{sub}</p>}
    </div>
  )
}

function SectionHead({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="mb-3">
      <h2 className="font-bold text-base" style={{ color: 'var(--text)' }}>{title}</h2>
      {sub && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub}</p>}
    </div>
  )
}

function AustraliaMap({ bySite }: { bySite: { state: string; count: number }[] }) {
  const stateMap: Record<string, number> = {}
  for (const s of bySite) if (s.state) stateMap[s.state] = (stateMap[s.state] || 0) + s.count
  const max = Math.max(1, ...Object.values(stateMap))

  return (
    <div className="card">
      <SectionHead title="Incidents by Jurisdiction" />
      <div className="grid grid-cols-4 gap-2">
        {AU_STATES.map(s => {
          const count = stateMap[s.code] || 0
          const intensity = count / max
          const bg = count === 0 ? 'var(--bg)' : intensity > 0.7 ? 'var(--red-light)' : intensity > 0.3 ? '#fffbeb' : '#f0fdf4'
          const fg = count === 0 ? 'var(--text-muted)' : intensity > 0.7 ? 'var(--red)' : intensity > 0.3 ? 'var(--amber)' : 'var(--green)'
          return (
            <div key={s.code} className="rounded-xl p-3 text-center" style={{ background: bg, border: '1px solid var(--border)' }}>
              <p className="font-bold text-lg" style={{ color: fg }}>{count}</p>
              <p className="text-xs font-semibold mt-0.5" style={{ color: fg }}>{s.code}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)', fontSize: '10px' }}>{s.name}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function LeadingPanel({ data }: { data: any }) {
  if (!data) return null
  const items = [
    { label: 'Audits Completed', value: data.audits_completed, colour: 'var(--green)' },
    { label: 'Audit Pass Rate', value: data.audit_pass_rate !== null ? `${data.audit_pass_rate}%` : '—', colour: data.audit_pass_rate >= 80 ? 'var(--green)' : 'var(--amber)' },
    { label: 'Permits Issued', value: data.permits_issued, colour: 'var(--accent)' },
    { label: 'CAs Closed', value: data.corrective_actions_closed, colour: 'var(--green)' },
    { label: 'CAs Overdue', value: data.corrective_actions_overdue, colour: data.corrective_actions_overdue > 0 ? 'var(--red)' : 'var(--green)' },
    { label: 'CA Closure Rate', value: data.ca_closure_rate !== null ? `${data.ca_closure_rate}%` : '—', colour: data.ca_closure_rate >= 80 ? 'var(--green)' : 'var(--amber)' },
    { label: 'Near Misses', value: data.near_misses_reported, colour: 'var(--amber)' },
    { label: 'Expired Licences', value: data.expired_competencies, colour: data.expired_competencies > 0 ? 'var(--red)' : 'var(--green)' },
    { label: 'Expiring (90 days)', value: data.expiring_competencies, colour: data.expiring_competencies > 0 ? 'var(--amber)' : 'var(--green)' },
    { label: 'Overdue Inspections', value: data.overdue_asset_inspections, colour: data.overdue_asset_inspections > 0 ? 'var(--red)' : 'var(--green)' },
  ]
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {items.map(it => (
        <div key={it.label} className="card text-center py-4">
          <p className="text-2xl font-bold" style={{ color: it.colour }}>{it.value ?? '—'}</p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{it.label}</p>
        </div>
      ))}
    </div>
  )
}

const SEV_COLOURS: Record<string, string> = {
  critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#22c55e',
}
const CAT_COLOURS = ['#6366f1', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444']

export default function MetricsDashboard() {
  const [period, setPeriod] = useState<Period>('ytd')
  const [rates, setRates] = useState<any>(null)
  const [trend, setTrend] = useState<any[]>([])
  const [breakdown, setBreakdown] = useState<any>(null)
  const [leading, setLeading] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    const { start, end } = periodDates(period)
    Promise.all([
      api.get(`/metrics/all-rates?start=${start}&end=${end}`),
      api.get('/metrics/monthly-trend?months=12'),
      api.get(`/metrics/breakdown?start=${start}&end=${end}`),
      api.get(`/metrics/leading-full?start=${start}&end=${end}`),
    ]).then(([r, t, b, l]) => {
      setRates(r.data)
      setTrend(t.data)
      setBreakdown(b.data)
      setLeading(l.data)
    }).finally(() => setLoading(false))
  }, [period])

  const PERIODS: { key: Period; label: string }[] = [
    { key: '30d', label: '30 days' },
    { key: '90d', label: '90 days' },
    { key: 'ytd', label: 'YTD' },
    { key: '1y', label: '12 months' },
  ]

  const { start, end } = periodDates(period)

  return (
    <div className="space-y-6">
      {/* Header + period selector */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Metrics & Performance</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {start} → {end}
          </p>
        </div>
        <div className="flex rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className="px-4 py-2 text-sm font-medium transition-all"
              style={{
                background: period === p.key ? 'var(--accent)' : 'var(--white)',
                color: period === p.key ? '#fff' : 'var(--text)',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading metrics...</div>
      ) : (
        <>
          {/* Lagging KPIs */}
          <div>
            <SectionHead title="Lagging Indicators" sub="Recordable injury frequency rates per 1,000,000 hours worked" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KPICard
                label="TRIFR"
                value={rates?.trifr}
                unit="/ M hrs"
                colour={severityColour(rates?.trifr, 'trifr')}
                sub="Total Recordable"
              />
              <KPICard
                label="LTIFR"
                value={rates?.ltifr}
                unit="/ M hrs"
                colour={severityColour(rates?.ltifr, 'ltifr')}
                sub="Lost Time"
              />
              <KPICard
                label="MTIFR"
                value={rates?.mtifr}
                unit="/ M hrs"
                colour="var(--text)"
                sub="Medical Treatment"
              />
              <KPICard
                label="Near Miss Rate"
                value={rates?.near_miss_rate}
                unit="/ M hrs"
                colour="var(--amber)"
                sub="Near misses"
              />
            </div>
          </div>

          {/* Days since LTI */}
          <div className="card flex items-center justify-between px-6 py-5" style={{ background: rates?.days_since_lti > 90 ? 'var(--green-light, #f0fdf4)' : 'var(--amber-light, #fffbeb)', border: `1px solid ${rates?.days_since_lti > 90 ? 'var(--green)' : 'var(--amber)'}` }}>
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Days Since Last Lost Time Injury</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {rates?.days_since_lti === null ? 'No LTIs on record' : `Keep the streak going — safety is everyone's responsibility`}
              </p>
            </div>
            <p className="text-6xl font-black" style={{ color: severityColour(rates?.days_since_lti, 'days') }}>
              {rates?.days_since_lti ?? '—'}
            </p>
          </div>

          {/* 12-month trend chart */}
          {trend.length > 0 && (
            <div className="card">
              <SectionHead title="12-Month Trend" sub="Monthly incident frequency rates" />
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trend} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                  <Tooltip
                    contentStyle={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                    formatter={(v: any, name: string) => [v ?? '—', name.toUpperCase()]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="trifr" name="TRIFR" stroke="#ef4444" strokeWidth={2} dot={false} connectNulls />
                  <Line type="monotone" dataKey="ltifr" name="LTIFR" stroke="#f97316" strokeWidth={2} dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Monthly incident volume */}
          {trend.length > 0 && (
            <div className="card">
              <SectionHead title="Monthly Incident Volume" />
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={trend} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="injuries" name="Injuries" fill="#ef4444" stackId="a" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="near_misses" name="Near Misses" fill="#f59e0b" stackId="a" />
                  <Bar dataKey="total_incidents" name="Total" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {breakdown && (
            <div className="grid md:grid-cols-2 gap-4">
              {/* By category */}
              {breakdown.by_category?.length > 0 && (
                <div className="card">
                  <SectionHead title="By Category" />
                  <div className="space-y-2">
                    {breakdown.by_category.map((c: any, i: number) => {
                      const max = breakdown.by_category[0]?.count || 1
                      return (
                        <div key={c.category}>
                          <div className="flex justify-between text-sm mb-0.5">
                            <span className="capitalize" style={{ color: 'var(--text)' }}>{c.category?.replace('_', ' ') || 'Unknown'}</span>
                            <span className="font-semibold" style={{ color: 'var(--text)' }}>{c.count}</span>
                          </div>
                          <div className="h-2 rounded-full" style={{ background: 'var(--border)' }}>
                            <div
                              className="h-2 rounded-full transition-all"
                              style={{ width: `${(c.count / max) * 100}%`, background: CAT_COLOURS[i % CAT_COLOURS.length] }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* By severity */}
              {breakdown.by_severity?.length > 0 && (
                <div className="card">
                  <SectionHead title="By Severity" />
                  <div className="space-y-2">
                    {breakdown.by_severity.map((s: any) => {
                      const max = Math.max(...breakdown.by_severity.map((x: any) => x.count), 1)
                      return (
                        <div key={s.ai_severity}>
                          <div className="flex justify-between text-sm mb-0.5">
                            <span className="capitalize" style={{ color: 'var(--text)' }}>{s.ai_severity}</span>
                            <span className="font-semibold" style={{ color: SEV_COLOURS[s.ai_severity] || 'var(--text)' }}>{s.count}</span>
                          </div>
                          <div className="h-2 rounded-full" style={{ background: 'var(--border)' }}>
                            <div
                              className="h-2 rounded-full transition-all"
                              style={{ width: `${(s.count / max) * 100}%`, background: SEV_COLOURS[s.ai_severity] || 'var(--text-muted)' }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  <div className="mt-4 pt-3 grid grid-cols-3 gap-3 text-center text-xs" style={{ borderTop: '1px solid var(--border)' }}>
                    <div>
                      <p className="font-bold" style={{ color: 'var(--text)' }}>{breakdown.total_days_lost}</p>
                      <p style={{ color: 'var(--text-muted)' }}>Days lost</p>
                    </div>
                    <div>
                      <p className="font-bold" style={{ color: 'var(--red)' }}>{breakdown.notifiable_events}</p>
                      <p style={{ color: 'var(--text-muted)' }}>Notifiable</p>
                    </div>
                    <div>
                      <p className="font-bold" style={{ color: 'var(--red)' }}>{breakdown.sif_events}</p>
                      <p style={{ color: 'var(--text-muted)' }}>SIF events</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* By site */}
          {breakdown?.by_site?.length > 0 && (
            <div className="card">
              <SectionHead title="By Site" />
              <div className="space-y-2">
                {breakdown.by_site.slice(0, 8).map((s: any, i: number) => {
                  const max = breakdown.by_site[0]?.count || 1
                  return (
                    <div key={s.site || i}>
                      <div className="flex justify-between text-sm mb-0.5">
                        <span style={{ color: 'var(--text)' }}>{s.site || 'Unknown site'}{s.state ? ` (${s.state})` : ''}</span>
                        <span className="font-semibold" style={{ color: 'var(--text)' }}>{s.count}</span>
                      </div>
                      <div className="h-2 rounded-full" style={{ background: 'var(--border)' }}>
                        <div className="h-2 rounded-full" style={{ width: `${(s.count / max) * 100}%`, background: 'var(--accent)' }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Australia map */}
          {breakdown?.by_state && <AustraliaMap bySite={breakdown.by_state} />}

          {/* Leading indicators */}
          <div>
            <SectionHead title="Leading Indicators" sub="Proactive safety measures for the selected period" />
            <LeadingPanel data={leading} />
          </div>
        </>
      )}
    </div>
  )
}
